import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

export const PAGE_HEADING_ID = 'page-heading'

/** Id of the focusable (`tabindex=-1`) `<main>` element in the app shell. */
export const MAIN_CONTENT_ID = 'main-content'

export const APP_NAME = 'Mango Voice'

/** First path segment of each main-shell page, mapped to its nav label key. */
const PAGE_LABEL_KEYS = {
  calls: 'nav.calls',
  'missed-calls': 'nav.missedCalls',
  voicemails: 'nav.voicemail',
  messaging: 'nav.text',
  faxes: 'nav.fax',
  contacts: 'nav.contacts',
  'call-analytics': 'nav.callAnalytics',
  'web-phone': 'nav.webPhone',
  reporting: 'nav.reporting',
  settings: 'nav.settings',
  hotels: 'nav.hotels',
  status: 'nav.status',
  admin: 'baseSettings.admin',
} as const

type PageSegment = keyof typeof PAGE_LABEL_KEYS

/**
 * Identifies the top-level page for a pathname. In-page changes such as
 * `/voicemails` → `/voicemails/42` keep the same key, so they don't move focus.
 */
export const getPageKey = (pathname: string): PageSegment | undefined => {
  const [firstSegment] = pathname.split('/').filter(Boolean)
  return firstSegment && firstSegment in PAGE_LABEL_KEYS
    ? (firstSegment as PageSegment)
    : undefined
}

export const buildDocumentTitle = (pageName?: string, pbxName?: string) => {
  const parts = [pageName, pbxName].filter(Boolean)
  return parts.length > 0 ? `${parts.join(' – ')} | ${APP_NAME}` : APP_NAME
}

const isInsideDialog = (element: Element) =>
  element.closest('[role="dialog"], [role="alertdialog"]') != null

let returnFocusTarget: HTMLElement | null = null

/**
 * Focuses `element` instead of the page heading after the next change of
 * top-level page, e.g. the control that opened a route-based dialog. Falls
 * back to the page heading if the element can't take focus by then.
 */
export const returnFocusAfterPageChange = (element: HTMLElement | null) => {
  returnFocusTarget = element
}

/**
 * Keeps `document.title` in sync with the current page and, after a
 * navigation to a different top-level page, moves focus to the page heading
 * so screen readers announce the new page and Tab continues from there.
 *
 * @returns the translated name of the current page, for the page `h1`.
 */
export const useRouteFocusAndTitle = ({
  pbxName,
  mainContentId,
}: {
  pbxName?: string
  mainContentId: string
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const pageKey = getPageKey(pathname)
  const pageName = pageKey ? t(PAGE_LABEL_KEYS[pageKey]) : undefined
  const previousPageKeyRef = useRef<PageSegment | undefined | null>(null)

  useEffect(() => {
    document.title = buildDocumentTitle(pageName, pbxName)
  }, [pageName, pbxName])

  useEffect(() => {
    const previousPageKey = previousPageKeyRef.current
    previousPageKeyRef.current = pageKey
    // Skip the first render and the index route's redirect to the default page.
    if (!previousPageKey || previousPageKey === pageKey) return

    const requestedTarget = returnFocusTarget
    returnFocusTarget = null
    if (requestedTarget) {
      requestedTarget.focus({ preventScroll: true })
      // Ignored if the element has left the document, is hidden or is inert.
      if (document.activeElement === requestedTarget) return
    }

    const activeElement = document.activeElement
    const mainContent = document.getElementById(mainContentId)
    // Respect pages that already placed focus in their own content or dialogs.
    if (
      activeElement &&
      activeElement !== document.body &&
      (mainContent?.contains(activeElement) || isInsideDialog(activeElement))
    ) {
      return
    }

    const focusTarget = document.getElementById(PAGE_HEADING_ID) ?? mainContent
    focusTarget?.focus({ preventScroll: true })
  }, [pageKey, mainContentId])

  return { pageName }
}
